import React, { useState } from "react";
import { TextField, Grid, Typography, Alert } from "@mui/material";
import SimpleDialog from "./SimpleDialog";
import { login } from "../../../services/authService";
import { useAuth } from "../../Context/AuthContext";

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
  onLoginSuccess?: () => void;
  onSwitchToRegister?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({
  open,
  onClose,
  onLoginSuccess,
  onSwitchToRegister,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { dispatch } = useAuth();

  const handleLogin = async () => {
    if (!email.trim() || !password) {
      setError("Please enter both email and password");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const tokenData = await login(email, password);

      // Update auth context
      dispatch({
        type: "set_login",
        value: { isLoggedIn: true },
      });

      // Clear form
      setEmail("");
      setPassword("");
      setError(null);

      // Call success callback if provided
      if (onLoginSuccess) {
        onLoginSuccess();
      }

      // Close modal
      onClose();
    } catch (err: any) {
      console.error("Login error:", err);
      if (err.response?.status === 401) {
        setError("Invalid email or password");
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError("Login failed. Please check your connection and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setPassword("");
    setError(null);
    setIsLoading(false);
    onClose();
  };

  const loginContent = () => (
    <Grid container spacing={3} style={{ marginTop: "2px", minWidth: "300px" }}>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <Typography variant="body1" style={{ marginBottom: "16px" }}>
          Please log in to save your artwork to your gallery.
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          autoFocus
          fullWidth
          label="Email or Username"
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          onKeyPress={(e) => {
            if (e.key === "Enter" && password.trim()) {
              handleLogin();
            }
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
          onKeyPress={(e) => {
            if (e.key === "Enter" && email.trim()) {
              handleLogin();
            }
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography
          variant="body2"
          style={{ fontSize: "0.9em", color: "#666" }}
        >
          Don't have an account?{" "}
          <button
            onClick={onSwitchToRegister}
            style={{
              color: "#1976d2",
              textDecoration: "none",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
              fontSize: "inherit",
            }}
            disabled={isLoading}
          >
            Create one here
          </button>
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <SimpleDialog
      open={open}
      title="Login to Save Your Art"
      content={loginContent()}
      actionTitle="Login"
      actionCB={handleLogin}
      actionUseLoading={true}
      actionIsLoading={isLoading}
      handleClose={handleClose}
      disableClose={isLoading}
    />
  );
};

export default LoginModal;
